/**
 * THE BOUNTY BAND'S MINIMIZE STATE (#4600) — shared by the town-square band and the
 * client's lite port, so both read and write the same cookie.
 *
 * There is no dismiss: while she has bounties to do, the band is always there. She may
 * minimize it to a tab, and it stays minimized until she expands it — or until a bounty
 * she has not seen arrives. "Seen" is the bounty AND its period, so a daily bounty's new
 * day reopens the band once, and finishing one never does.
 *
 * The cookie holds the keys on screen when she minimized, nothing more, so it is pruned
 * on every write.
 */

export const BAND_COOKIE = 'ah_bounty_band';

/** A year: minimized stays minimized until she says otherwise. */
export const BAND_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** How long one bounty shows before the band turns to the next. */
export const BAND_ROTATE_MS = 10_000;

/** One bounty in one period. */
export function bandKeys(bounties: ReadonlyArray<{ id: string; periodKey: string }>): string[] {
  return bounties.map((bounty) => `${bounty.id}:${bounty.periodKey}`);
}

export function serializeMinimized(keys: readonly string[]): string {
  return encodeURIComponent(keys.join('|'));
}

/** The keys she minimized over, or null when there is no cookie (or it is unreadable). */
export function parseMinimized(value: string | null | undefined): string[] | null {
  if (!value) return null;
  try {
    return decodeURIComponent(value).split('|').filter(Boolean);
  } catch {
    return null;
  }
}

/** Minimized holds only while every bounty on offer is one she already minimized over. */
export function shouldStayMinimized(saved: readonly string[] | null, current: readonly string[]): boolean {
  if (!saved) return false;
  const seen = new Set(saved);
  return current.every((key) => seen.has(key));
}

/** The parent domain in production, so town-square and the client app share one cookie. */
export function cookieDomain(hostname: string): string | undefined {
  if (hostname === 'arthelper.com' || hostname.endsWith('.arthelper.com')) return '.arthelper.com';
  return undefined;
}

/** The cookie's value, out of a `document.cookie` string. */
export function readBandCookie(cookies: string): string | null {
  const prefix = `${BAND_COOKIE}=`;
  const found = cookies.split('; ').find((part) => part.startsWith(prefix));
  if (!found) return null;
  return found.slice(prefix.length);
}

/** A `document.cookie` assignment that writes the keys, or clears the cookie when null. */
export function bandCookie(keys: readonly string[] | null, hostname: string): string {
  const domain = cookieDomain(hostname);
  const scope = `; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
  if (!keys) return `${BAND_COOKIE}=${scope}; max-age=0`;
  return `${BAND_COOKIE}=${serializeMinimized(keys)}${scope}; max-age=${BAND_COOKIE_MAX_AGE}`;
}
